import React from "react";

import "../../../layout/leaflet.scss";
import MapIcon from "../../../../assets/image/marker-icon.png";

const LEAFLET_SRC = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const LEAFLET_INTEGRITY = "sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=";

let leafletPromise = null;

function ensureLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = LEAFLET_SRC;
    script.integrity = LEAFLET_INTEGRITY;
    script.crossOrigin = "";
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = err => reject(err);
    document.body.appendChild(script);
  });

  return leafletPromise;
}

export default class LeafletMap extends React.Component {
  constructor(props) {
    super(props);
    this.containerId = `pt-map-${Math.random()
      .toString(36)
      .slice(2)}`;
    this.map = null;
    this.marker = null;
    this._destroyed = false;
  }

  componentDidMount() {
    ensureLeaflet().then(L => {
      if (!this._destroyed) this.init(L);
    });
  }

  init(L) {
    const { lat, lng, zoom = 13, editable } = this.props;
    const center = [lat ?? 0, lng ?? 0];

    this.L = L;
    this.map = L.map(this.containerId).setView(center, zoom);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.map);

    const icon = L.icon({
      iconUrl: MapIcon,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
    });

    this.marker = L.marker(center, { icon, draggable: !!editable }).addTo(this.map);

    /* An optional recorded track — the visit page draws the merchandiser's GPS trail from
       `visit.meta`. Fitting the bounds is what makes the trail visible: centred on the point at a
       fixed zoom, a 3km walk is either off-screen or a dot. */
    this.drawTrack(L);

    if (editable) {
      this.marker.on("dragend", () => {
        const { lat: newLat, lng: newLng } = this.marker.getLatLng();
        if (this.props.onChange) this.props.onChange({ lat: newLat, lng: newLng });
      });

      this.map.on("click", e => {
        this.marker.setLatLng(e.latlng);
        if (this.props.onChange) this.props.onChange({ lat: e.latlng.lat, lng: e.latlng.lng });
      });
    }

    setTimeout(() => this.map && this.map.invalidateSize(), 0);
  }

  drawTrack(L) {
    const { track } = this.props;
    if (!Array.isArray(track) || track.length < 2) return;
    /* Leaflet draws polylines as real SVG in the DOM, so the stroke can come from a token via a
       class — no hex here, and the track follows the theme. */
    this.track = L.polyline(track, { className: "lf-track" }).addTo(this.map);
    this.map.fitBounds(this.track.getBounds(), { padding: [24, 24] });
  }

  componentDidUpdate(prevProps) {
    const { lat, lng } = this.props;
    if (!this.map || !this.marker) return;
    if (lat === null || lat === undefined || lng === null || lng === undefined) return;
    if (prevProps.lat === lat && prevProps.lng === lng) return;

    this.marker.setLatLng([lat, lng]);
    this.map.setView([lat, lng], this.map.getZoom());
  }

  componentWillUnmount() {
    this._destroyed = true;
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  render() {
    const { height = 320 } = this.props;
    return <div id={this.containerId} className="pt-map" style={{ height, width: "100%" }} />;
  }
}
